import { createContext, useContext, useEffect, useMemo, type ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BadgeCheck,
  BookOpen,
  CalendarCheck,
  DatabaseZap,
  Factory,
  Home,
  Mail,
  TriangleAlert,
  Users,
  type LucideProps,
} from 'lucide-react';

/**
 * Identidade e navegação da aplicação.
 *
 * Este arquivo é o ponto único que um próximo sistema do weloze.lab precisa
 * trocar para reaproveitar o mesmo layout: nome da aplicação e registro de
 * rotas. O `AppShell` e o `Breadcrumbs` leem daqui e não conhecem nada
 * específico do Portal do Fornecedor.
 */

export const APP_NAME = 'Portal do Fornecedor';

/** Sufixo do título da aba. Mantém a marca visível fora do próprio site. */
export const APP_TITLE_SUFFIX = `${APP_NAME} · weloze.lab`;

interface RouteMeta {
  /** Rótulo exibido na trilha e no título da aba. */
  label: string;
  /**
   * `false` para trechos de caminho que agrupam rotas mas não têm tela própria
   * (ex.: `/perfil`). Eles aparecem na trilha, mas sem link — clicar levaria a
   * um 404, o que é pior do que não poder clicar.
   */
  navigable?: boolean;
}

/**
 * Registro de rotas. Cresce junto com as fatias: quando a tela do IDC entrar,
 * é aqui que ela ganha nome na trilha e na aba.
 *
 * Um segmento iniciado por `:` casa com qualquer valor — é assim que
 * `/admin/fornecedores/<uuid>` acha o rótulo sem precisar de uma entrada por
 * fornecedor.
 */
const ROUTES: Record<string, RouteMeta> = {
  '/': { label: 'Início', navigable: true },
  '/perfil': { label: 'Perfil', navigable: false },
  '/perfil/senha': { label: 'Alterar senha', navigable: true },
  '/admin': { label: 'Administração', navigable: false },
  '/admin/fornecedores': { label: 'Fornecedores', navigable: true },
  '/admin/fornecedores/:storeId': { label: 'Ficha do fornecedor', navigable: true },
  '/admin/certificacoes': { label: 'Certificações', navigable: true },
  '/admin/competencias': { label: 'Competências', navigable: true },
  '/admin/competencias/:year/:month': { label: 'Competência', navigable: true },
  // Abaixo de competências, e não ao lado: a média semestral é uma leitura das
  // competências publicadas, e a trilha tinha de poder voltar para elas.
  '/admin/competencias/semestre': { label: 'Semestre', navigable: false },
  '/admin/competencias/semestre/:year/:half': { label: 'Semestre', navigable: true },
  '/admin/importacao': { label: 'Sincronização', navigable: true },
  '/admin/emails': { label: 'E-mails', navigable: true },
  '/admin/usuarios': { label: 'Usuários', navigable: true },
  '/minha-empresa': { label: 'Minha empresa', navigable: true },
  '/certificados': { label: 'Meus certificados', navigable: true },
  '/documentos': { label: 'Documentos', navigable: true },
  // Navegável, e é o que faz a trilha da RNC ter volta: `/rncs` é uma tela de
  // verdade agora, e não um trecho de caminho sem destino.
  '/rncs': { label: 'RNCs', navigable: true },
  '/rncs/:rncId': { label: 'RNC', navigable: true },
  '/relatorio': { label: 'Relatório', navigable: false },
  '/relatorio/:storeId/:year/:month': { label: 'Relatório da competência', navigable: true },
};

/**
 * Seções do menu principal.
 *
 * Junto com `APP_NAME` e `ROUTES`, é o que um próximo sistema do weloze.lab
 * troca para reaproveitar o layout — o `AppShell` renderiza esta lista sem saber
 * o que ela contém.
 *
 * Cresce por fatia: RNCs, certificações, competências, ranking, importação e
 * configurações entram aqui conforme forem existindo (PLAN §7).
 */
export interface Section {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  /** `true` esconde a seção de quem tem papel de fornecedor. */
  welozeOnly?: boolean;
  /**
   * `true` mostra a seção **só** para o fornecedor.
   *
   * "Meus certificados" é a tela do fornecedor sobre a própria loja; a Weloze
   * tem a fila de validação em `/admin/certificacoes`, que responde outra
   * pergunta. Oferecer as duas a ela seria oferecer dois caminhos para lugares
   * diferentes com nomes parecidos.
   */
  supplierOnly?: boolean;
  /**
   * `true` mostra a seção **só** para o administrador.
   *
   * Mais estreito que `welozeOnly`: o perfil de consulta enxerga a carteira
   * inteira, mas a lista de usuários é o mapa de quem acessa o quê — quem não
   * pode conceder acesso também não precisa saber quem tem.
   */
  adminOnly?: boolean;
}

export const SECTIONS: Section[] = [
  { to: '/', label: 'Início', icon: Home },
  { to: '/minha-empresa', label: 'Minha empresa', icon: Factory, supplierOnly: true },
  { to: '/certificados', label: 'Meus certificados', icon: BadgeCheck, supplierOnly: true },
  // Sem restricao de papel: o manual existe para ser lido pelos dois lados.
  { to: '/documentos', label: 'Documentos', icon: BookOpen },
  { to: '/admin/fornecedores', label: 'Fornecedores', icon: Factory, welozeOnly: true },
  // Sem restrição de papel: é por aqui que o fornecedor chega ao 8D dele.
  { to: '/rncs', label: 'RNCs', icon: TriangleAlert },
  { to: '/admin/certificacoes', label: 'Certificações', icon: BadgeCheck, welozeOnly: true },
  { to: '/admin/competencias', label: 'Competências', icon: CalendarCheck, welozeOnly: true },
  { to: '/admin/importacao', label: 'Sincronização', icon: DatabaseZap, welozeOnly: true },
  { to: '/admin/emails', label: 'E-mails', icon: Mail, welozeOnly: true },
  { to: '/admin/usuarios', label: 'Usuários', icon: Users, welozeOnly: true, adminOnly: true },
];

export interface Crumb {
  label: string;
  /** Ausente no último item e nos trechos sem tela própria. */
  to?: string;
}

/** Transforma um segmento desconhecido em algo legível: `plano-acao` → `Plano acao`. */
function humanize(segment: string): string {
  const text = decodeURIComponent(segment).replace(/[-_]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Procura o rótulo de um caminho, aceitando segmentos dinâmicos.
 *
 * A busca é exata primeiro: `/admin/fornecedores` não pode casar com o padrão
 * de outra rota só porque tem o mesmo número de segmentos.
 */
function lookup(path: string): RouteMeta | undefined {
  const exact = ROUTES[path];
  if (exact) return exact;

  const segments = path.split('/').filter(Boolean);
  for (const [pattern, meta] of Object.entries(ROUTES)) {
    const parts = pattern.split('/').filter(Boolean);
    if (parts.length !== segments.length) continue;
    if (parts.every((part, i) => part.startsWith(':') || part === segments[i])) return meta;
  }
  return undefined;
}

/**
 * Deriva a trilha do caminho atual, acumulando segmento a segmento.
 *
 * Devolve lista vazia na raiz e **em qualquer tela que já seja uma seção do
 * menu**: ali a aba ativa acima já diz onde a pessoa está, e a trilha só
 * repetiria a informação numa terceira faixa. Ela volta a aparecer assim que se
 * desce um nível — na ficha, que é onde ela serve para voltar.
 *
 * `dynamicLabel` substitui o rótulo do último item. É o que permite a ficha
 * mostrar "ACZ INOX 000012/0001" em vez do UUID da URL — o nome só é conhecido
 * depois que a tela carrega os dados, então ele vem de lá para cá.
 */
export function buildBreadcrumbs(pathname: string, dynamicLabel?: string | null): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];
  if (SECTIONS.some((section) => section.to === pathname)) return [];

  const crumbs: Crumb[] = [{ label: ROUTES['/']!.label, to: '/' }];

  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    const isLast = index === segments.length - 1;

    if (isLast) {
      const meta = lookup(path);
      crumbs.push({ label: dynamicLabel ?? meta?.label ?? humanize(segment) });
      return;
    }

    // No meio do caminho, só vale rota **literal** — `ROUTES[path]`, não
    // `lookup`. Trecho que existe apenas como `:parâmetro` de outra rota é
    // parâmetro, não lugar: em `/relatorio/<uuid>/2026/1` os dois do meio são o
    // id da loja e o ano, e a trilha mostrava o UUID cru e um "2026" morto.
    //
    // Casar por padrão aqui seria pior que não casar: `/admin/competencias/
    // semestre/2026` satisfaz `:year/:month` — "semestre" passaria por ano — e a
    // trilha ganharia um "Competência" com link para uma tela que não existe.
    const meta = ROUTES[path];
    if (!meta) return;

    crumbs.push({
      label: meta.label,
      ...(meta.navigable ? { to: path } : {}),
    });
  });

  return crumbs;
}

/**
 * Rótulo publicado pela tela atual.
 *
 * Existe porque só a tela sabe o nome do recurso que está mostrando, e a trilha
 * é renderizada acima dela no `AppShell`.
 */
export const PageLabelContext = createContext<{
  label: string | null;
  setLabel: (label: string | null) => void;
}>({ label: null, setLabel: () => {} });

export function useBreadcrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const { label } = useContext(PageLabelContext);
  return useMemo(() => buildBreadcrumbs(pathname, label), [pathname, label]);
}

/**
 * Publica o nome do recurso desta tela na trilha e no título da aba.
 * Limpa ao desmontar, senão o nome antigo vazaria para a tela seguinte.
 */
export function usePageLabel(label: string | null | undefined): void {
  const { setLabel } = useContext(PageLabelContext);
  useEffect(() => {
    setLabel(label ?? null);
    return () => setLabel(null);
  }, [label, setLabel]);
}

/** Rótulo da tela atual, usado no título da aba. */
export function useRouteLabel(): string | null {
  const { pathname } = useLocation();
  const { label } = useContext(PageLabelContext);
  return label ?? lookup(pathname)?.label ?? null;
}

/**
 * Mantém o `document.title` coerente com a rota.
 *
 * Sem isso, o histórico e as abas do navegador ficam com o mesmo texto em toda
 * tela — e quem trabalha com várias abas abertas não distingue uma da outra.
 */
export function useDocumentTitle(): void {
  const label = useRouteLabel();

  useEffect(() => {
    document.title =
      label && label !== ROUTES['/']!.label
        ? `${label} · ${APP_TITLE_SUFFIX}`
        : APP_TITLE_SUFFIX;
  }, [label]);
}
